import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useServerApi } from "../hooks/userServerApi";
import { MultiSelect } from "@mantine/core";
import _ from "lodash";
import { useTranslation } from "react-i18next";

const toId = (value, valueField) => {
  if (value == null) return null;
  if (typeof value === "object") {
    const id = value[valueField];
    return id != null ? String(id) : null;
  }
  return String(value);
};

const toOption = (row, valueField, getLabel) => ({
  value: String(_.get(row, valueField)),
  label: getLabel(row),
});

function MultiRemoteSelect({
  form,
  name,
  label,
  value: valueProp,
  onChange: onChangeProp,
  apiEntity,
  valueField = "_id",
  labelField = "code",
  preQuery,
  searchFields = ["code", "name"],
  sort,
  labelRender,
  placeholder = "Search and select",
  pageSize = 10,
  required = false,
  ...props
}) {
  const [remoteOptions, setRemoteOptions] = useState([]);
  const { t: translate } = useTranslation();
  const [searchQuery, setSearchQuery] = useState({});
  const [api] = useServerApi();

  const controlledValue =
    form && name != null ? _.get(form?.values, name) : valueProp;

  const getLabel = useCallback(
    (data) => {
      if (labelRender) return labelRender(data);
      if (labelField) return _.get(data, labelField);
      return "";
    },
    [labelRender, labelField]
  );

  const selectedIds = useMemo(() => {
    const arr = Array.isArray(controlledValue) ? controlledValue : [];
    return [...new Set(arr.map((v) => toId(v, valueField)).filter(Boolean))];
  }, [controlledValue, valueField]);

  // Seed options from populated form values so Mantine MultiSelect can render
  // selections before async search returns (it hides values missing from data).
  const valueOptions = useMemo(() => {
    const arr = Array.isArray(controlledValue) ? controlledValue : [];
    return arr
      .map((v) => {
        if (v && typeof v === "object" && v[valueField] != null) {
          return toOption(v, valueField, getLabel);
        }
        const id = toId(v, valueField);
        return id ? { value: id, label: id } : null;
      })
      .filter(Boolean);
  }, [controlledValue, valueField, getLabel]);

  const options = useMemo(() => {
    const merged = new Map();
    valueOptions.forEach((o) => merged.set(o.value, o));
    remoteOptions.forEach((o) => merged.set(o.value, o));
    return Array.from(merged.values());
  }, [valueOptions, remoteOptions]);

  const fetchData = async () => {
    try {
      const result1 = await api.search({
        apiEntity,
        pageSize,
        currentPage: 1,
        sort,
        searchQuery,
        byAggregation: false,
      });

      const o1 = (result1?.docs ?? []).map((r) => toOption(r, valueField, getLabel));

      if (!selectedIds.length) {
        setRemoteOptions(o1);
        return;
      }

      const result2 = await api.search({
        apiEntity,
        pageSize,
        currentPage: 1,
        sort,
        searchQuery: { [valueField]: { $in: selectedIds } },
        byAggregation: false,
      });

      const o2 = (result2?.docs ?? []).map((r) => toOption(r, valueField, getLabel));

      setRemoteOptions(_.uniqBy([...o1, ...o2], (o) => o.value));
    } catch (e) {
      // keep existing options on error
    }
  };

  const buildSearchQuery = (searchText, fields) => {
    if (!searchText?.trim()) {
      return preQuery ?? {};
    }

    const searchArr = fields?.map((field) => ({
      [field]: { $regex: searchText.trim(), $options: "i" },
    }));

    if (!preQuery) return { $or: searchArr };
    return { $and: [preQuery, { $or: searchArr }] };
  };

  const handleSearchChange = (searchText) => {
    setSearchQuery(buildSearchQuery(searchText, searchFields));
  };

  const handleChange = (values) => {
    const next = values ?? [];
    if (form && name != null) {
      form?.markUserEdited?.();
      form.setFieldValue(name, next);
    }
    onChangeProp?.(next);
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery, selectedIds.join("|")]);

  return (
    <MultiSelect
      {...props}
      required={required}
      label={label}
      data={options}
      value={selectedIds}
      limit={pageSize}
      searchable
      clearable
      onChange={handleChange}
      onSearchChange={handleSearchChange}
      placeholder={translate(placeholder)}
      nothingFound="No results"
    />
  );
}

export default MultiRemoteSelect;
